import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { normalizeNodeId } from '@gt/para/shared/para-node-id.util';
import { ParaApiRootService } from './para-api-root.service';
import { ParaStudentHistoryResponseDto } from '../models/para-student-history.dto';

@Injectable({ providedIn: 'root' })
export class ParaStudentHistoryService {
	private readonly httpByKey = new Map<string, Observable<ParaStudentHistoryResponseDto | null>>();

	constructor(private readonly httpClient: HttpClient, private readonly paraApi: ParaApiRootService) {}

	getStudentHistory(
		nodeId: unknown,
		participantId: unknown,
		assessmentId?: string | null
	): Observable<ParaStudentHistoryResponseDto | null> {
		const nid = normalizeNodeId(nodeId);
		const pid = participantId != null ? Number(participantId) : null;
		if (nid == null || pid == null || Number.isNaN(pid)) {
			return of(null);
		}
		const aid = (assessmentId || '').trim();
		const key = aid ? `${nid}::${pid}::${aid}` : `${nid}::${pid}`;
		let shared = this.httpByKey.get(key);
		if (!shared) {
			let params = new HttpParams();
			if (aid) params = params.set('assessmentId', aid);
			shared = this.httpClient
				.get<ParaStudentHistoryResponseDto>(this.paraApi.reportsUrl(`getStudentHistory/nodeId/${nid}/participantId/${pid}`), {
					params,
				})
				.pipe(shareReplay({ bufferSize: 1, refCount: true }));
			this.httpByKey.set(key, shared);
		}
		return shared;
	}

	invalidate(nodeId: unknown, participantId: unknown): void {
		const nid = normalizeNodeId(nodeId);
		const pid = participantId != null ? Number(participantId) : null;
		if (nid == null || pid == null || Number.isNaN(pid)) return;
		const prefix = `${nid}::${pid}`;
		for (const k of [...this.httpByKey.keys()]) {
			if (k === prefix || k.indexOf(prefix + '::') === 0) {
				this.httpByKey.delete(k);
			}
		}
	}

	/**
	 * Deletes one persisted item score (student drawer trash on a question row).
	 * POST /para/progress/deleteHistoryItem/nodeId/:nodeId
	 */
	deleteHistoryItem(
		nodeId: unknown,
		participantId: unknown,
		itemScoreId: number,
		assessmentId: string | null
	): Observable<{ success?: boolean }> {
		const nid = normalizeNodeId(nodeId);
		const pid = participantId != null ? Number(participantId) : null;
		if (nid == null || pid == null || Number.isNaN(pid) || itemScoreId <= 0) {
			return throwError(() => new Error('Invalid deleteHistoryItem parameters'));
		}
		const body: Record<string, string | number> = {
			participantId: pid,
			itemScoreId,
		};
		const aid = (assessmentId || '').trim();
		if (aid) body['assessmentId'] = aid;
		return this.httpClient
			.post<{ success?: boolean }>(this.paraApi.progressUrl(`deleteHistoryItem/nodeId/${nid}`), body)
			.pipe(tap(() => this.invalidate(nodeId, participantId)));
	}

	/**
	 * Deletes an entire assessment result row (header trash for that test date).
	 * POST /para/progress/deleteHistoryResult/nodeId/:nodeId
	 */
	deleteHistoryResult(
		nodeId: unknown,
		participantId: unknown,
		paraAssessmentResultId: number,
		assessmentId: string | null
	): Observable<{ success?: boolean }> {
		const nid = normalizeNodeId(nodeId);
		const pid = participantId != null ? Number(participantId) : null;
		if (nid == null || pid == null || Number.isNaN(pid) || paraAssessmentResultId <= 0) {
			return throwError(() => new Error('Invalid deleteHistoryResult parameters'));
		}
		const body: Record<string, string | number> = {
			participantId: pid,
			paraAssessmentResultId,
		};
		const aid = (assessmentId || '').trim();
		if (aid) body['assessmentId'] = aid;
		return this.httpClient
			.post<{ success?: boolean }>(this.paraApi.progressUrl(`deleteHistoryResult/nodeId/${nid}`), body)
			.pipe(tap(() => this.invalidate(nodeId, participantId)));
	}
}
